import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ShoppingBagIcon,
  ShieldCheckIcon,
  CubeIcon,
  RefreshIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/outline';
import AdvancedFilter, {
  AppliedFilter as AdvAppliedFilter,
  FilterDef as AdvFilterDef,
} from 'components/AdvancedFilter';
import { SearchBox } from 'components/Search';
import { NonNegativeNumberInput } from 'components/NonNegativeNumberInput';
import { useQuery, useLazyQuery } from '@apollo/client';
import { columns, shipmentStatus } from './data/orders-data';
import { OrdersTable } from './OrdersTable';
import { Order } from 'types/order';
import { GET_ORDERS_COUNT, FILTER_ORDERS } from 'gql/queries';
import { withNotification } from 'components/Notification';
import { presetToLocalRange, customToLocalRange } from 'utils/date';
import { apiErrorHandler, NotificationTypes } from 'utils';
import { useStore } from 'context/store-context';
import { withRequestReturnTo } from 'utils/requestNavigation';

const ORDER_SORT_COLUMNS = [
  'createdAt',
  'customerName',
  'customerEmail',
  'total',
];
const ORDER_SORT_OPTIONS = [
  ['createdAt:desc', 'Newest first'],
  ['createdAt:asc', 'Oldest first'],
  ['total:desc', 'Highest total'],
  ['total:asc', 'Lowest total'],
  ['customerName:asc', 'Customer name A–Z'],
  ['customerName:desc', 'Customer name Z–A'],
  ['customerEmail:asc', 'Customer email A–Z'],
  ['customerEmail:desc', 'Customer email Z–A'],
];
const ORDER_DATE_PRESETS = [
  'today',
  'past_week',
  'past_month',
  'past_quarter',
  'past_year',
];
const isOrderDate = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};
const isOrderDateFilter = (values: string[]): boolean => {
  if (values.length === 1 && values[0].startsWith('token:'))
    return ORDER_DATE_PRESETS.includes(values[0].slice(6));
  if (values.length === 2 && values[0] === 'preset')
    return ORDER_DATE_PRESETS.includes(values[1]);
  return (
    values.length === 3 &&
    values[0] === 'custom' &&
    isOrderDate(values[1]) &&
    isOrderDate(values[2]) &&
    values[1] <= values[2]
  );
};

const readOrdersFilters = (params: URLSearchParams): AdvAppliedFilter[] => {
  const labels: Record<string, string> = {
    protectionStatus: 'Delivery Guarantee status',
    trackingStatus: 'Shipment status',
    orderEdited: 'Order edited',
    treePlanted: 'Tree planted',
    dateRange: 'Date range',
  };
  try {
    const saved = JSON.parse(params.get('filters') || 'null');
    if (Array.isArray(saved)) {
      return saved
        .filter(
          (filter) =>
            filter &&
            Object.prototype.hasOwnProperty.call(labels, filter.key) &&
            Array.isArray(filter.values) &&
            filter.values.every(
              (value: unknown) => typeof value === 'string',
            ) &&
            (filter.key !== 'dateRange' || isOrderDateFilter(filter.values)),
        )
        .map((filter) => ({
          key: filter.key,
          label: labels[filter.key],
          values: filter.values,
        }));
    }
  } catch {
    // Older bookmarked order filters remain valid without a saved filter array.
  }

  const parsed: AdvAppliedFilter[] = [];
  const edited = (
    params.get('orderEdited') || params.get('edited-orders')
  )?.toLowerCase();
  if (edited && ['yes', 'true', '1', 'no', 'false', '0'].includes(edited)) {
    parsed.push({
      key: 'orderEdited',
      label: labels.orderEdited,
      values: [['yes', 'true', '1'].includes(edited) ? 'Yes' : 'No'],
    });
  }
  const min = params.get('processed_at_min');
  const max = params.get('processed_at_max');
  if (min) {
    const token = min.replace(/^token:/, '');
    if (ORDER_DATE_PRESETS.includes(token)) {
      parsed.push({
        key: 'dateRange',
        label: labels.dateRange,
        values: ['preset', token],
      });
    } else if (isOrderDate(min) && (!max || (isOrderDate(max) && min <= max))) {
      parsed.push({
        key: 'dateRange',
        label: labels.dateRange,
        values: ['custom', min, max || min],
      });
    }
  }
  return parsed;
};

const readOrdersPage = (params: URLSearchParams) => {
  const page = Number(params.get('page'));
  return Number.isSafeInteger(page) && page > 0 ? page : 1;
};

export const Orders = withNotification(({ showNotification }: any) => {
  const limit = 20;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [allOrders, setAllOrders] = useState<any | []>([]);
  const [ordersCount, setOrdersCount] = useState(0);
  const [filterInput, setFilterInput] = useState<any>({});
  const [filtersReady, setFiltersReady] = useState(false);
  const [searchValue, setSearchValue] = useState(
    () => searchParams.get('q') || '',
  );
  const [page, setPage] = useState(() => readOrdersPage(searchParams));
  const [offset, setOffset] = useState(
    () => (readOrdersPage(searchParams) - 1) * limit,
  );
  const [sort, setSort] = useState(() => ({
    column: ORDER_SORT_COLUMNS.includes(searchParams.get('sort') || '')
      ? searchParams.get('sort')!
      : 'createdAt',
    direction:
      searchParams.get('direction') === 'asc'
        ? ('asc' as const)
        : ('desc' as const),
  }));
  const [advancedFilters, setAdvancedFilters] = useState<AdvAppliedFilter[]>(
    () => readOrdersFilters(searchParams),
  );
  const hasBuiltFilters = React.useRef(false);

  // Date helpers are centralized in utils/dateRange.ts; this inline block remains only for legacy support.
  const { storeId, storeProperties } = useStore();
  const returnLocation = `/orders${
    searchParams.toString() ? `?${searchParams}` : ''
  }`;

  const {
    data: countData,
    loading: countsLoading,
    error: countsError,
    refetch: refetchCounts,
  } = useQuery(GET_ORDERS_COUNT, {
    variables: {
      totalOrdersWhere: {
        storeId: { _eq: storeId },
      },
      unprotectedOrdersWhere: {
        storeId: { _eq: storeId },
        protectionTotal: { _is_null: true },
      },
      protectedOrdersWhere: {
        storeId: { _eq: storeId },
        protectionTotal: { _is_null: false },
      },
    },
    fetchPolicy: 'network-only',
    skip: !storeId,
  });

  const [filterOrders, { loading, error }] = useLazyQuery(FILTER_ORDERS, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      setAllOrders(data?.orders);
      const count = data?.ordersAggregate?.aggregate?.count ?? 0;
      setOrdersCount(count);
      const lastPage = Math.max(1, Math.ceil(count / limit));
      if (page > lastPage) {
        setPage(lastPage);
        setOffset((lastPage - 1) * limit);
      }
    },
    onError(error) {
      const newError = apiErrorHandler(error);
      showNotification(NotificationTypes.error, newError?.message);
    },
  });

  const loadOrders = useCallback(() => {
    if (storeId)
      return filterOrders({
        variables: {
          filters: { ...filterInput, storeId: { _eq: storeId } },
          limit,
          offset,
          order_by: [
            {
              [sort.column]: sort.direction,
            },
          ],
        },
      });
  }, [filterInput, filterOrders, storeId, offset, sort]);

  useEffect(() => {
    if (filtersReady) void loadOrders();
  }, [filtersReady, loadOrders]);

  useEffect(() => {
    if (hasBuiltFilters.current) {
      setPage(1);
      setOffset(0);
    }
    hasBuiltFilters.current = true;

    // Build a fresh base to avoid stale clauses when removing filters
    const next: any = {};
    if (searchValue && searchValue.trim().length) {
      next._or = [
        { number: { _ilike: `%${searchValue}%` } },
        { customerEmail: { _ilike: `%${searchValue}%` } },
        { customerName: { _ilike: `%${searchValue}%` } },
      ];
    }

    const negativeClauses: any[] = [];

    advancedFilters.forEach((f) => {
      if (f.key === 'dateRange') {
        const vals = f.values;
        // token from legacy 'token:xxx'
        if (vals.length === 1 && vals[0].startsWith('token:')) {
          const token = vals[0].replace('token:', '') as any;
          const r = presetToLocalRange(token);
          if (r) {
            const exist = Array.isArray(next._and) ? next._and : [];
            next._and = [
              ...exist,
              { createdAt: { _gte: r.startISO } },
              { createdAt: { _lt: r.endISO } },
            ];
          }
        } else if (vals.length >= 2 && vals[0] === 'preset') {
          const token = vals[1] as any;
          const r = presetToLocalRange(token);
          if (r) {
            const exist = Array.isArray(next._and) ? next._and : [];
            next._and = [
              ...exist,
              { createdAt: { _gte: r.startISO } },
              { createdAt: { _lt: r.endISO } },
            ];
          }
        } else if (vals.length >= 3 && vals[0] === 'custom') {
          const from = vals[1];
          const to = vals[2];
          if (from && to) {
            const r = customToLocalRange(from, to);
            const exist = Array.isArray(next._and) ? next._and : [];
            next._and = [
              ...exist,
              { createdAt: { _gte: r.startISO } },
              { createdAt: { _lt: r.endISO } },
            ];
          }
        }
      }
      if (f.key === 'protectionStatus') {
        const mapped = f.values.map((v) => getMappedFilterOption(v));
        if (mapped.length === 1) next.protectionStatus = { _eq: mapped[0] };
        else if (mapped.length > 1) next.protectionStatus = { _in: mapped };
      }
      if (f.key === 'trackingStatus') {
        const mapped = f.values.map((v) => getMappedShipmentStatus(v));
        if (mapped.length) next.trackingStatus = { _in: mapped };
      }
      if (f.key === 'orderEdited') {
        const yes = f.values.includes('Yes');
        const no = f.values.includes('No');
        if (yes && no) {
          // no constraint
        } else if (yes) {
          next.activities = { eventType: { _neq: 'ORDER_CREATED' } };
        } else if (no) {
          negativeClauses.push({
            activities: { eventType: { _neq: 'ORDER_CREATED' } },
          });
        }
      }
      if (f.key === 'treePlanted') {
        const yes = f.values.includes('Yes');
        const no = f.values.includes('No');
        if (yes && no) {
          // no constraint
        } else if (yes) {
          // require jsonb to have key 'id'
          next.treeDetails = { _has_key: 'id' } as any;
        } else if (no) {
          negativeClauses.push({ treeDetails: { _has_key: 'id' } });
        }
      }
    });

    if (negativeClauses.length) {
      const existingAnd = Array.isArray(next._and) ? next._and : [];
      next._and = [
        ...existingAnd,
        ...negativeClauses.map((expr) => ({ _not: expr })),
      ];
    }

    setFilterInput(next);
    setFiltersReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, searchValue]);

  useEffect(() => {
    try {
      const params = new URLSearchParams(searchParams);
      if (searchValue) params.set('q', searchValue);
      else params.delete('q');
      if (advancedFilters.length)
        params.set('filters', JSON.stringify(advancedFilters));
      else params.delete('filters');
      if (page > 1) params.set('page', String(page));
      else params.delete('page');
      if (sort.column !== 'createdAt') params.set('sort', sort.column);
      else params.delete('sort');
      if (sort.direction !== 'desc') params.set('direction', sort.direction);
      else params.delete('direction');
      // remove unsupported keys and previous date params
      [
        'protectionStatus',
        'trackingStatus',
        'treePlanted',
        'dateFrom',
        'dateTo',
        'processed_at_min',
        'processed_at_max',
        'fp',
        'ft',
        'fo',
        'fe',
        'ftr',
      ].forEach((k) => params.delete(k));
      // clear order edited variants before (re)setting
      params.delete('orderEdited');
      params.delete('edited-orders');
      const editedF = advancedFilters.find((f) => f.key === 'orderEdited');
      if (editedF) {
        // Persist as 'Yes' | 'No' to match existing links
        if (editedF.values.includes('Yes')) params.set('orderEdited', 'Yes');
        else if (editedF.values.includes('No')) params.set('orderEdited', 'No');
      }
      // date range tokens/custom
      const dateF = advancedFilters.find((f) => f.key === 'dateRange');
      if (dateF) {
        const vals = dateF.values;
        if (vals.length === 1 && vals[0].startsWith('token:')) {
          params.set('processed_at_min', vals[0].replace('token:', ''));
          params.delete('processed_at_max');
        } else if (vals.length >= 2 && vals[0] === 'preset') {
          params.set('processed_at_min', vals[1]);
          params.delete('processed_at_max');
        } else if (vals.length >= 3 && vals[0] === 'custom') {
          const from = vals[1];
          const to = vals[2] || vals[1];
          params.set('processed_at_min', from);
          params.set('processed_at_max', to);
        }
      }
      const currentStr = searchParams.toString();
      const nextStr = params.toString();
      if (currentStr !== nextStr) setSearchParams(params, { replace: true });
    } catch (e) {
      // ignore URL param sync errors
    }
  }, [advancedFilters, searchValue, page, sort, searchParams, setSearchParams]);

  const search = () => {
    const currentSearch = filterInput._or?.[0]?.number?._ilike;
    if (
      currentSearch === `%${searchValue}%` ||
      (!currentSearch && !searchValue.trim())
    )
      return;
    setFilterInput({
      ...filterInput,
      _or: [
        { number: { _ilike: `%${searchValue}%` } },
        { customerEmail: { _ilike: `%${searchValue}%` } },
        { customerName: { _ilike: `%${searchValue}%` } },
      ],
    });
  };

  const getMappedFilterOption = (option: string): string => {
    if (option === 'Guaranteed') return 'Protected';
    else if (option === 'Not Guaranteed') return 'Unprotected';
    else if (option === 'Issue Filed') return 'Claim Filed';
    else return option;
  };

  const getMappedShipmentStatus = (status: string): string => {
    const trackStatus = shipmentStatus.find((s) => s.label === status);
    return trackStatus?.value ?? '';
  };

  const onPageChange = (pageNumber: number) => {
    setPage(pageNumber);
    if (pageNumber < page) {
      if (pageNumber - 1 === 0) {
        return setOffset(0);
      }
      return setOffset(limit * (pageNumber - 1));
    }
    return setOffset(limit * (pageNumber - 1));
  };

  // Date range is handled via AdvancedFilter 'dateRange'

  const onFileClaim = (order: Order) => {
    navigate('/claims/new-claim', {
      state: { order, returnLocation },
    });
  };

  const handleOrderByColumn = (column: string, direction: string) => {
    if (
      !ORDER_SORT_COLUMNS.includes(column) ||
      !['asc', 'desc'].includes(direction)
    )
      return;
    setSort({ column, direction: direction as 'asc' | 'desc' });
    setPage(1);
    setOffset(0);
  };

  const stats = [
    {
      name: 'Total Orders',
      value: countData?.totalOrders?.aggregate?.total,
      icon: ShoppingBagIcon,
    },
    {
      name: 'Guaranteed',
      value: countData?.protectedOrders?.aggregate?.total,
      icon: ShieldCheckIcon,
    },
    {
      name: 'Not guaranteed',
      value: countData?.unprotectedOrders?.aggregate?.total,
      icon: CubeIcon,
    },
  ];
  const filterOptions = ['Guaranteed', 'Not Guaranteed', 'Issue Filed'];

  const advancedFilterDefs: AdvFilterDef[] = [
    {
      key: 'protectionStatus',
      label: 'Delivery Guarantee status',
      type: 'multiselect',
      options: filterOptions.map((o) => ({ label: o, value: o })),
    },
    {
      key: 'trackingStatus',
      label: 'Shipment status',
      type: 'multiselect',
      options: shipmentStatus.map((s) => ({ label: s.label, value: s.label })),
    },
    {
      key: 'orderEdited',
      label: 'Order edited',
      type: 'multiselect',
      options: [
        { label: 'Yes', value: 'YES' },
        { label: 'No', value: 'NO' },
      ],
    },
    {
      key: 'treePlanted',
      label: 'Tree planted',
      type: 'multiselect',
      options: [
        { label: 'Yes', value: 'YES' },
        { label: 'No', value: 'NO' },
      ],
    },
    {
      key: 'dateRange',
      label: 'Date',
      type: 'dateRange',
      options: [],
    },
  ];

  const totalPages = Math.max(1, Math.ceil(ordersCount / limit));
  const hasFilters = Boolean(searchValue || advancedFilters.length);
  const clearFilters = () => {
    setSearchValue('');
    setAdvancedFilters([]);
  };
  const pageButtonClass =
    'inline-flex items-center justify-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40';

  const onViewOrder = (order: Order) => {
    navigate(withRequestReturnTo(`/orders/${order.id}`, returnLocation));
  };

  return (
    <div className="mx-auto max-w-7xl space-y-5 px-4 pb-8 sm:px-6 md:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-blue-50 p-3 text-blue-700">
            <ShoppingBagIcon aria-hidden="true" className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-gray-900">
              All Orders
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Review orders, shipments, and related requests.
            </p>
          </div>
        </div>
        <button
          type="button"
          aria-label="Refresh orders"
          className={pageButtonClass}
          disabled={loading || !storeId}
          onClick={() => {
            void loadOrders();
            void refetchCounts();
          }}
        >
          <RefreshIcon
            aria-hidden="true"
            className={`h-4 w-4 ${loading ? 'motion-safe:animate-spin' : ''}`}
          />
          Refresh
        </button>
      </div>
      <section aria-label="Order summary">
        <dl className="grid grid-cols-3 gap-2 sm:gap-3">
          {stats.map(({ name, value, icon: Icon }) => (
            <div
              key={name}
              className="min-w-0 rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4"
            >
              <dt className="flex min-h-[2rem] items-center justify-between gap-2 text-xs font-medium text-gray-600 sm:min-h-0">
                {name}
                <Icon
                  aria-hidden="true"
                  className="hidden h-4 w-4 flex-shrink-0 text-gray-400 sm:block"
                />
              </dt>
              <dd className="mt-2 text-2xl font-semibold tracking-tight tabular-nums text-gray-900">
                {countsLoading || countsError || value == null
                  ? '—'
                  : Number(value).toLocaleString()}
              </dd>
            </div>
          ))}
        </dl>
        {countsError && (
          <p className="mt-2 text-sm text-gray-600">
            Totals unavailable.{' '}
            <button
              type="button"
              className="font-medium text-blue-700 hover:underline"
              onClick={() => {
                void refetchCounts();
              }}
            >
              Retry totals
            </button>
          </p>
        )}
      </section>
      <section
        aria-label="Order filters"
        className="space-y-4 rounded-xl border border-gray-200 bg-gray-50 p-4 shadow-sm sm:p-5"
      >
        <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-[minmax(0,1fr)_14rem]">
          <label className="min-w-0 text-xs font-medium text-gray-600">
            <span className="mb-1.5 block">Search orders</span>
            <SearchBox
              searchValue={searchValue}
              onChange={setSearchValue}
              placeholder="Search by order no, name or email"
              search={search}
            />
          </label>
          <label className="min-w-0 text-xs font-medium text-gray-600">
            Sort orders
            <select
              value={`${sort.column}:${sort.direction}`}
              onChange={(event) => {
                const [column, direction] = event.target.value.split(':');
                handleOrderByColumn(column, direction);
              }}
              className="mt-1.5 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm font-normal text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {ORDER_SORT_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <AdvancedFilter
          filters={advancedFilterDefs}
          applied={advancedFilters}
          onChange={setAdvancedFilters}
        />
      </section>
      {loading ? (
        <div
          role="status"
          className="rounded-xl border border-gray-200 bg-white px-5 py-12 text-center text-sm text-gray-500"
        >
          Loading orders…
        </div>
      ) : error ? (
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700"
        >
          Orders could not be loaded.{' '}
          <button
            type="button"
            className="font-semibold underline"
            onClick={() => {
              void loadOrders();
            }}
          >
            Retry orders
          </button>
        </div>
      ) : allOrders.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white px-5 py-12 text-center">
          <ShoppingBagIcon
            aria-hidden="true"
            className="mx-auto mb-3 h-8 w-8 text-gray-300"
          />
          <h3 className="text-sm font-semibold text-gray-900">
            {hasFilters ? 'No matching orders' : 'No orders yet'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {hasFilters
              ? 'Try a different search or clear your filters.'
              : 'Orders will appear here after they sync.'}
          </p>
          {hasFilters && (
            <button
              type="button"
              className="mt-4 text-sm font-medium text-blue-700 hover:underline"
              onClick={clearFilters}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <OrdersTable
          orders={
            allOrders?.length > 0
              ? [...allOrders].map((item: Order) => ({
                  ...item,
                  onFileClaim: () => onFileClaim(item),
                  currency: storeProperties?.currency,
                  onViewOrder: () => onViewOrder(item),
                  returnLocation,
                }))
              : []
          }
          columns={columns}
          emptyMessage={
            searchValue
              ? `No search results matches "${searchValue}"`
              : 'No data'
          }
          orderByOptions={ORDER_SORT_COLUMNS}
          sortColumn={sort.column}
          sortDirection={sort.direction}
          handleSelectedColumn={handleOrderByColumn}
        />
      )}
      {!loading && !error && ordersCount > 0 && (
        <nav
          aria-label="Orders pagination"
          className="flex flex-wrap items-center justify-between gap-4 text-sm text-gray-600"
        >
          <p className="tabular-nums">
            {(page - 1) * limit + 1}–{Math.min(page * limit, ordersCount)} of{' '}
            {ordersCount.toLocaleString()} orders
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              aria-label="First page"
              className={pageButtonClass}
              disabled={page <= 1}
              onClick={() => onPageChange(1)}
            >
              <ChevronDoubleLeftIcon aria-hidden="true" className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Previous page"
              className={pageButtonClass}
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
            >
              <ChevronLeftIcon aria-hidden="true" className="h-4 w-4" />
              Previous
            </button>
            <label className="flex items-center gap-2">
              Page
              <NonNegativeNumberInput
                min={1}
                max={totalPages}
                value={page}
                onChange={(event) => {
                  const next = Number(event.target.value);
                  if (Number.isInteger(next) && next >= 1 && next <= totalPages)
                    onPageChange(next);
                }}
                className="w-14 rounded-lg border border-gray-300 bg-white px-2 py-2 text-center text-sm tabular-nums text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </label>
            <span className="tabular-nums">of {totalPages}</span>
            <button
              type="button"
              aria-label="Next page"
              className={pageButtonClass}
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
            >
              Next
              <ChevronRightIcon aria-hidden="true" className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Last page"
              className={pageButtonClass}
              disabled={page >= totalPages}
              onClick={() => onPageChange(totalPages)}
            >
              <ChevronDoubleRightIcon aria-hidden="true" className="h-4 w-4" />
            </button>
          </div>
        </nav>
      )}
    </div>
  );
});
